import React, { useState } from "react";
import useFileNameLength from "../../../../hooks/useFileNameLength";
import { WORKING_RIGHTS_OPTIONS } from "../../../../models/Onboarding/onboarding-values";
import { ICandidateDetails } from "../../../../store/superadmin/types";
import { formatFileName } from "../../../../utils/stringUtils";
import CustomButton from "../../../UI/Buttons/CustomButton";
import Download from "../../../UI/Icons/Download";
import ViewDocument from "./Popup/ViewDocument";


interface PersonalDetailProps {
  showPersonalDetails: boolean;
  reviewCandidateData: ICandidateDetails | null;
  handleDownloadDocument: (url: string) => void;
}

const PersonalDetails: React.FC<PersonalDetailProps> = ({
  showPersonalDetails,
  reviewCandidateData,
  handleDownloadDocument,
}) => {
  const { maxLength, preserveEndChars } = useFileNameLength();
  const [showModal, setShowModal] = useState<boolean>(false);
  const [document, setDocument] = useState<string | null>(null);
  const [documentName, setDocumentName] = useState<string | null>(null);

  const onClickClose = () => {
    setDocument(null);
    setDocumentName(null);
    setShowModal(false);
  }

  const getWorkingRightsText = (rights: string) => {
    switch (rights) {
      case "australian_citizen":
        return WORKING_RIGHTS_OPTIONS.australian_citizen;
      case "australian_permanent_resident":
        return WORKING_RIGHTS_OPTIONS.australian_permanent_resident;
      case "australian_visa_holder":
        return WORKING_RIGHTS_OPTIONS.australian_visa_holder;
      case "new_zealand_citizen":
        return WORKING_RIGHTS_OPTIONS.new_zealand_citizen;
      case "dont_have_australia_working_rights":
        return WORKING_RIGHTS_OPTIONS.dont_have_australia_working_rights;
      default:
        return "No data";
    }
  }

  const handleOpenDoc = (resume_name: string, resume: string) => {
    const resumeName = resume_name;
    const isDocFile = resumeName?.endsWith(".doc") || resumeName?.endsWith(".docx");

    const viewerUrl = isDocFile ? `https://docs.google.com/gview?url=${encodeURIComponent(resume)}&embedded=true` : resume;
    setDocument(viewerUrl);
    setDocumentName(resume_name);
    setShowModal(!false);
  }

  return (
    <>
      <ViewDocument
        type={"Resume View"}
        showModal={showModal}
        onClickClose={onClickClose}
        document={document}
        documentName={documentName}
      />

      <div
        className={`d-flex flex-column gap-20 fw-400 text-black text-break ${
          showPersonalDetails ? "" : "d-none"
        }`}
      >
        <div className="fs-22">Personal Details</div>
        <div className="d-flex flex-column gap-10">
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">Mobile Number</div>
            <div className="fs-16">{reviewCandidateData?.phone_number ?? "No data"}</div>
          </div>
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">Email</div>
            <div className="fs-16">{reviewCandidateData?.email}{" "}{reviewCandidateData?.email_verified_at === null ? "(Not verified)" : ""}</div>
          </div>
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">Home Address</div>
            <div className="fs-16">
              {}
              {reviewCandidateData?.address ?? "No data"}
            </div>
          </div>
          <div className="d-flex flex-column gap-5">
            {reviewCandidateData?.profile?.resume !== null ? (
              <>
                <div className="fs-13 text-grey-medium">Resume</div>
                <div className="d-flex">
                  <CustomButton
                    variant={"primary"}
                    type="button"
                    className="btn-theme bg-off-white text-black"
                    onClick={() => handleOpenDoc(reviewCandidateData?.profile?.resume_name ?? "", reviewCandidateData?.profile?.resume ?? "")}
                  >
                    <Download />
                    {formatFileName(reviewCandidateData?.profile?.resume_name ?? "No data", maxLength, preserveEndChars)}
                  </CustomButton>
                </div>
              </>
            ) : (
              <>
                <div className="fs-13 text-grey-medium">Resume</div>
                <div className="fs-16">Document not uploaded</div>
              </>
            )}
          </div>
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">
              Are you interested in permanent opportunities as well as casual shifts?
            </div>
            <div className="fs-16">
              {reviewCandidateData?.profile?.permanent_employment
                ? "Yes"
                : "No"}
            </div>
          </div>
          <div className="d-flex flex-column gap-5">
            <div className="fs-13 text-grey-medium">
              Do you have current working rights in Australia?
            </div>
            <div className="fs-16">
              {
                getWorkingRightsText(reviewCandidateData?.profile?.working_rights || "")
              }
            </div>
          </div>
        </div>
      </div>
    </>
  );
};

export default PersonalDetails;
